'use client'

import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Handshake } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/utils/styles/cn'
import { AdPerformanceScoreBadge } from '@/features/research/ad-performance-score-badge'
import { IResearchedAd } from '@/api/research-jobs/ads/get-researched-ads-query'
import { IClassNameProps } from '@/utils/typings/class-name-props'
import { memo } from 'react'
import { useCompanyPageLink } from '@/features/research/company/use-company-page-link'
import { useResearchAdsContext } from '@/features/research/research-ads/research-ads-context'

type IResearchAdCardHeaderProps = IClassNameProps & {
    ad: IResearchedAd
    /** Bulk-selection mode routes every card click to selection — the brand link must not steal it. */
    selectionMode?: boolean
}

/**
 * Card header: brand identity on the left, the estimated-performance score badge as the single
 * headline metric on the right. Research-recency is deliberately not surfaced on the card — the
 * preview modal's research-meta cells carry it for anyone who needs it.
 */
export const ResearchAdCardHeader = memo(({ ad, className, selectionMode = false }: IResearchAdCardHeaderProps) => {
    const { config } = useResearchAdsContext()
    return (
        <CardHeader className={cn('p-2', className)}>
            <CardTitle className="grid grid-cols-[1fr_auto] items-center gap-1.5">
                <ResearchAdCardBrandBlock ad={ad} suppressLink={config.readOnly || selectionMode} />
                <div className="flex items-center gap-1">
                    <PaidPartnershipBadge collaboration={ad.collaboration ?? null} />
                    <AdPerformanceScoreBadge score={ad.metrics.performanceScore} startDate={ad.metrics.startDate} />
                </div>
            </CardTitle>
        </CardHeader>
    )
})

ResearchAdCardHeader.displayName = 'ResearchAdCardHeader'

/**
 * The disclosed paid partnership, badged. Absent for a self-tagged ad and for anything Meta
 * never disclosed — the card shows no row for what it does not know.
 */
export function PaidPartnershipBadge({
    collaboration,
    withPartnerName = false,
}: {
    collaboration: { partnerName: string | null } | null
    /** The modal has the room to name the partner inline; the card only has the icon's width. */
    withPartnerName?: boolean
}) {
    if (!collaboration) return null

    const label = collaboration.partnerName
        ? `Paid partnership with ${collaboration.partnerName}`
        : 'Paid partnership'

    return (
        <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
                <Badge variant="outline" className="h-5 shrink-0 gap-1 px-1.5 text-[11px] font-medium">
                    <Handshake className="h-3 w-3" />
                    {withPartnerName ? label : 'Paid partnership'}
                </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-64">{label}</TooltipContent>
        </Tooltip>
    )
}

/**
 * The card's brand identity block. With company pages enabled it links to the advertiser's
 * company page; on read-only surfaces, in bulk-selection mode, and on the advertiser's own
 * company page (a self-link would only reset the tab) it stays a plain label.
 */
export const ResearchAdCardBrandBlock = ({
    ad,
    suppressLink,
    className,
}: {
    ad: IResearchedAd
    suppressLink: boolean
    className?: string
}) => {
    const { getCompanyUrl } = useCompanyPageLink()
    const pathname = usePathname()
    const isOwnCompanyPage = pathname?.includes(`/research/companies/${ad.brand.uuid}`) ?? false
    const companyUrl = suppressLink || isOwnCompanyPage ? null : getCompanyUrl(ad.brand.uuid, { fromResearch: true })

    const content = (
        <>
            <Image
                key={ad.brand.logoFile?.url}
                src={ad.brand.logoFile?.url ?? ''}
                alt="Brand Logo"
                width={20}
                height={20}
                className="size-5 shrink-0 rounded-[4px] object-cover"
            />
            <span className="min-w-0 truncate text-sm font-medium">{ad.brand.name}</span>
        </>
    )

    if (!companyUrl) {
        return <div className={cn('flex min-w-0 items-center gap-2', className)}>{content}</div>
    }

    return (
        <Link
            href={companyUrl}
            onClick={(event) => event.stopPropagation()}
            className={cn('flex min-w-0 items-center gap-2 hover:underline underline-offset-2', className)}
            title={`Open ${ad.brand.name}'s company page`}
        >
            {content}
        </Link>
    )
}
